// ABOUTME: Admin-only page for managing users
// ABOUTME: Allows creating, listing, and deactivating users

import React, { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useUsers, useCreateUser, useDeactivateUser, useResetPassword, useChangeUserRole } from '@/hooks/useAdmin'
import type { UserRole } from '@/types/admin'

export const UserManagementPage: React.FC = () => {
  const { user } = useAuth()
  const { data: users, isLoading, error } = useUsers()
  const createUserMutation = useCreateUser()
  const deactivateUserMutation = useDeactivateUser()
  const resetPasswordMutation = useResetPassword()
  const changeRoleMutation = useChangeUserRole()

  const [isCreating, setIsCreating] = useState(false)
  const [newUsername, setNewUsername] = useState('')
  const [newUserRole, setNewUserRole] = useState<UserRole>('user')
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null)
  const [editingRoleUserId, setEditingRoleUserId] = useState<number | null>(null)
  const [selectedRole, setSelectedRole] = useState<UserRole>('user')

  // Only admins should see this page
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h1>
          <p className="text-gray-600">You do not have permission to access this page.</p>
        </div>
      </div>
    )
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newUsername.trim()) {
      alert('Username is required')
      return
    }

    try {
      const response = await createUserMutation.mutateAsync({
        username: newUsername,
        role: newUserRole,
      })

      // Show the generated password
      setGeneratedPassword(response.password)
      setNewUsername('')
      setNewUserRole('user')
      setIsCreating(false)
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Failed to create user'
      alert(`Error: ${errorMsg}`)
    }
  }

  const handleDeactivateUser = async (userId: number, username: string) => {
    if (!confirm(`Are you sure you want to deactivate user "${username}"?`)) {
      return
    }

    try {
      await deactivateUserMutation.mutateAsync(userId)
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Failed to deactivate user'
      alert(`Error: ${errorMsg}`)
    }
  }

  const handleResetPassword = async (userId: number, username: string) => {
    if (!confirm(`Are you sure you want to reset the password for user "${username}"?`)) {
      return
    }

    try {
      const response = await resetPasswordMutation.mutateAsync(userId)
      setGeneratedPassword(response.password)
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset password'
      alert(`Error: ${errorMsg}`)
    }
  }

  const handleStartRoleChange = (userId: number, currentRole: UserRole) => {
    setEditingRoleUserId(userId)
    setSelectedRole(currentRole)
  }

  const handleConfirmRoleChange = async (userId: number, username: string, oldRole: string) => {
    if (selectedRole === oldRole) {
      setEditingRoleUserId(null)
      return
    }

    try {
      await changeRoleMutation.mutateAsync({ userId, role: selectedRole })
      setEditingRoleUserId(null)
      alert(`Successfully changed role for "${username}" to ${selectedRole}`)
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Failed to change role'
      alert(`Error: ${errorMsg}`)
    }
  }

  const handleCancelRoleChange = () => {
    setEditingRoleUserId(null)
    setSelectedRole('user')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Error</h1>
          <p className="text-gray-600">Failed to load users</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
          <p className="mt-2 text-gray-600">Manage user accounts and permissions</p>
        </div>

        {/* Generated Password Alert */}
        {generatedPassword && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">User Created Successfully</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p className="font-bold">Save this password immediately - it won't be shown again!</p>
                  <div className="mt-2 bg-white p-3 rounded border border-yellow-200">
                    <code className="text-lg font-mono text-gray-900">{generatedPassword}</code>
                  </div>
                </div>
                <button
                  onClick={() => setGeneratedPassword(null)}
                  className="mt-3 text-sm font-medium text-yellow-800 hover:text-yellow-900"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create User Button */}
        {!isCreating && (
          <div className="mb-6">
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              + Create New User
            </button>
          </div>
        )}

        {/* Create User Form */}
        {isCreating && (
          <div className="mb-6 bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username (Email)
                </label>
                <input
                  type="email"
                  id="username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  id="role"
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value as UserRole)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="admin">Admin</option>
                  <option value="exco">Exco</option>
                  <option value="leads">Leads</option>
                  <option value="user">User</option>
                  <option value="staff">Staff</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Admin: Full access | Exco: All except user management | Leads: All except user management | User: View data | Staff: View data (no business recommendations)
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={createUserMutation.isPending}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                >
                  {createUserMutation.isPending ? 'Creating...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreating(false)
                    setNewUsername('')
                    setNewUserRole('user')
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users?.map((u) => (
                <tr key={u.id} className={!u.is_active ? 'bg-gray-50 opacity-60' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {u.username}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {editingRoleUserId === u.id ? (
                      <div className="flex items-center gap-2">
                        <select
                          value={selectedRole}
                          onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                          className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="admin">Admin</option>
                          <option value="exco">Exco</option>
                          <option value="leads">Leads</option>
                          <option value="user">User</option>
                          <option value="staff">Staff</option>
                        </select>
                        <button
                          onClick={() => handleConfirmRoleChange(u.id, u.username, u.role)}
                          disabled={changeRoleMutation.isPending}
                          className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:bg-gray-400"
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelRoleChange}
                          className="px-2 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                          u.role === 'exco' ? 'bg-indigo-100 text-indigo-800' :
                          u.role === 'leads' ? 'bg-green-100 text-green-800' :
                          u.role === 'staff' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {u.role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        u.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {u.created_by || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {u.is_active && u.username !== user?.username && (
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => handleStartRoleChange(u.id, u.role)}
                          disabled={editingRoleUserId !== null}
                          className="text-green-600 hover:text-green-900 disabled:text-gray-400"
                        >
                          Change Role
                        </button>
                        <button
                          onClick={() => handleResetPassword(u.id, u.username)}
                          disabled={resetPasswordMutation.isPending}
                          className="text-blue-600 hover:text-blue-900 disabled:text-gray-400"
                        >
                          Reset Password
                        </button>
                        <button
                          onClick={() => handleDeactivateUser(u.id, u.username)}
                          disabled={deactivateUserMutation.isPending}
                          className="text-red-600 hover:text-red-900 disabled:text-gray-400"
                        >
                          Deactivate
                        </button>
                      </div>
                    )}
                    {u.username === user?.username && (
                      <span className="text-gray-400 text-xs">Current User</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {users?.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No users found
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
